/**
 * Hydrated public dynamic config — SSR-safe synchronous access.
 *
 * Routes that need a public `/config/{key}` payload preloaded on the server
 * include `dynamicConfigMiddleware` in their middleware chain and merge the
 * resulting `dynamicConfigContext` into their loader return value (see
 * `routes/_app._index.tsx`). `HydratedDynamicConfigProvider` walks the
 * matched routes, picks up the payload from whichever match supplied it, and
 * injects it as a `<script>` tag in `<head>` so
 * `window.__HYDRATED_DYNAMIC_CONFIG__` is set before any client code runs.
 * `usePublicDynamicConfig` then uses it as SWR `fallbackData` and skips the
 * first-paint network round-trip.
 *
 * @see src/middleware/dynamic-config.middleware.server.ts
 * @see src/services/restful/dynamicConfig.ts
 */
import { useMatches } from 'src/utils/router/route'

// Inlined to avoid a cross-bundle `import type` from a `.server.ts` file —
// some bundlers (vitest browser mode here) still trace the path even for
// type-only imports, which can confuse module-graph invalidation.
type HydratedDynamicConfig = Record<string, unknown>

declare global {
  interface Window {
    __HYDRATED_DYNAMIC_CONFIG__?: HydratedDynamicConfig
  }
}

/**
 * Read a server-injected public dynamic-config payload.
 * Returns `undefined` on the server, or when the key wasn't preloaded.
 */
export function getHydratedDynamicConfig<T = unknown>(
  key: string,
): T | undefined {
  if (typeof window === 'undefined') return undefined
  return window.__HYDRATED_DYNAMIC_CONFIG__?.[key] as T | undefined
}

/**
 * Find the `dynamicConfig` payload provided by any matched route's loader.
 * Returns `undefined` when no matched route preloaded any keys for this
 * request (e.g. routes that don't include `dynamicConfigMiddleware`).
 */
function useMatchedDynamicConfig(): HydratedDynamicConfig | undefined {
  const matches = useMatches()
  for (const match of matches) {
    const data = match.data as
      | { dynamicConfig?: HydratedDynamicConfig }
      | undefined
    if (data?.dynamicConfig && Object.keys(data.dynamicConfig).length > 0) {
      return data.dynamicConfig
    }
  }
  return undefined
}

/**
 * Injects server-fetched public dynamic configs as an IIFE `<script>` tag.
 * The browser executes this script before React hydrates, so
 * `window.__HYDRATED_DYNAMIC_CONFIG__` is ready for synchronous reads.
 *
 * Place inside `<head>` in root.tsx Layout.
 */
export function HydratedDynamicConfigProvider() {
  const config = useMatchedDynamicConfig()
  if (!config) return null

  // Escape:
  //   `<` so `</script>` in payload doesn't close the script tag
  //   U+2028 / U+2029 — valid in JSON strings but JS line terminators
  //     that would break the IIFE if interpolated raw
  const safeJson = JSON.stringify(config)
    .replace(/</g, '\\u003c')
    .replaceAll('\u2028', '\\u2028')
    .replaceAll('\u2029', '\\u2029')

  return (
    <script
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: `(function(){window.__HYDRATED_DYNAMIC_CONFIG__=${safeJson}})()`,
      }}
    />
  )
}
